import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import Loader from "../../common/Loader";
import DetailsModal from "../../common/DetailsModal";
import {
    getMerchants,
    getMerchantById,
    deleteMerchantById,
} from "../../../services/merchantService";
import { getMerchantInvites } from "../../../services/onboardingService";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import CancelPopup from "../../common/CancelPopup";
import deleteImg from "../../../Assets/Images/deleteImg.svg";
import SearchBar from "../../common/SearchBar";
import debounce from "lodash.debounce";
import Confirmation from "../../common/OfferList/Confirmation";
import AddIcon from "../../../Assets/Icons/AddIcon.svg";
import PopupContent from "../../Fundraiser/FundraiserReferralListTable/PopupContent";

const InProcessMerchantList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = useSelector((state) => state?.merchantReducer?.loading);

    const [allMerchants, setAllMerchants] = useState();
    const [fullMerchantsList, setFullMerchantsList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [count, setCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [merchantDetails, setMerchantDetails] = useState(null);
    const [isMerchantDetailOpen, setIsMerchantDetailOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [merchantToDelete, setMerchantToDelete] = useState(null);

    const items = [
        {
            title: "Merchants",
        },
        {
            title: "In-Process Merchants",
        },
    ];

    const columns = [
        {
            name: "No.",
        },
        {
            name: "Name",
        },
        {
            name: "Date joined",
        },
        {
            name: "Email",
        },
        {
            name: "Contact",
        },
        {
            name: "Address",
        },
        {
            name: "Status",
        },
    ];

    // Function to check if a merchant is in-process (incomplete signup)
    // IMPORTANT: This logic must match the MerchantList component to ensure consistent filtering
    const isInProcess = (merchant) => {
        const status = merchant?.status?.toLowerCase();
        // Categorize everything that is not "approved" as "In-Process"
        return status !== "approved";
    };

    const updatePageSlice = (data, page) => {
        if (!data) return;
        const startIndex = (page - 1) * 50;
        const endIndex = startIndex + 50;
        setAllMerchants(data.slice(startIndex, endIndex));
    };

    const getMerchantsData = async (search) => {
        let searchParams = {
            limit: 1000, // Fetch all for client-side filtering
            // Note: Fetch all to filter client-side as backend might not map "in-process" status directly
            status: null,
            duration: null,
        };

        if (search && search.trim()) {
            const searchTerm = search.trim();
            const isContactNumber = /^[\d\s+\-()]+$/.test(searchTerm);
            const isDate = /^[\d/\-]+$/.test(searchTerm) && (searchTerm.includes('/') || searchTerm.includes('-'));

            if (isContactNumber) {
                searchParams.contactSearch = searchTerm;
            } else if (isDate) {
                searchParams.dateSearch = searchTerm;
            } else {
                searchParams.search = searchTerm;
            }
        }

        Object.keys(searchParams).forEach((key) =>
            searchParams[key] === null ? delete searchParams[key] : {}
        );

        const merchantsPromise = await dispatch(getMerchants(searchParams))?.unwrap();

        let combinedData = [];
        if (merchantsPromise?.success === true) {
            combinedData = [...(merchantsPromise?.data || [])];
        }

        // Also fetch invitations to include those who are in-process but not yet full merchants
        const invitesPromise = await dispatch(getMerchantInvites({ limit: 1000 }))?.unwrap();
        if (invitesPromise?.success === true) {
            try {
                const invitationsAsMerchants = (invitesPromise?.data || [])
                    // Include in Merchant List if they have a name OR have moved beyond the initial "pending" state.
                    .filter(invite => invite.businessName || invite.business_name || invite.status === "accepted" || invite.status === "completed" || invite.status === "approved")
                    .map(invite => ({
                        ...invite,
                        _id: invite._id || invite.id,
                        businessName: invite.businessName || invite.business_name || "Merchant (No Name)",
                        status: invite.status || "invited",
                        isFromInvitation: true
                    }));

                // Merge and avoid duplicates
                invitationsAsMerchants.forEach(invite => {
                    const email = (invite.email || invite.user?.email || "").toLowerCase();
                    if (!email) return;

                    const exists = combinedData.find(m =>
                        (m.email?.toLowerCase() === email) ||
                        (m.user?.email?.toLowerCase() === email) ||
                        (m.businessEmail?.toLowerCase() === email) ||
                        (m.business_email?.toLowerCase() === email)
                    );
                    if (!exists) {
                        combinedData.push(invite);
                    }
                });
            } catch (error) {
                console.error("Failed to fetch invitations for InProcess Merchant List:", error);
            }
        }

        if (combinedData.length > 0 || merchantsPromise?.success === true) {
            // Helper to check for the internal "Pending_..." placeholder strings
            const isInternalPlaceholder = (val) => typeof val === 'string' && val.startsWith("Pending_");

            // Filter to show only in-process merchants and exclude placeholder records
            const inProcessMerchants = combinedData.filter((merchant) => {
                const hasPlaceholder = isInternalPlaceholder(merchant.businessName) || isInternalPlaceholder(merchant.business_name);
                return isInProcess(merchant) && !hasPlaceholder;
            });

            // SORT BY DATE DESCENDING (Latest on top)
            inProcessMerchants.sort((a, b) => {
                const dateA = new Date(a.createdAt || a.created_at || a.notifiedAt || a.updatedAt || 0);
                const dateB = new Date(b.createdAt || b.created_at || b.notifiedAt || b.updatedAt || 0);
                return dateB - dateA;
            });

            setFullMerchantsList(inProcessMerchants);

            // Calculate total pages
            const total = Math.ceil((inProcessMerchants?.length || 0) / 50);
            setTotalPages(total || 1);
            setCount(inProcessMerchants?.length || 0);

            const newPage = (search || searchQuery) ? 1 : currentPage;
            setCurrentPage(newPage);
            updatePageSlice(inProcessMerchants, newPage);
        }
    };

    useEffect(() => {
        getMerchantsData(searchQuery);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (fullMerchantsList.length > 0) {
            updatePageSlice(fullMerchantsList, currentPage);
        }
    }, [currentPage]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // Scroll to top of merchant list when changing pages
        const container = document.getElementById('merchant-list-container');
        if (container) {
            container.scrollIntoView({ behavior: 'smooth' });
        } else {
            // Fallback to window scroll
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                getMerchantsData(query);
            }, 500),
        []
    );

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        debouncedSearch(value);
    };

    const fetchMerchantDetails = async (merchantId) => {
        try {
            const merchantDetailsPromise = await dispatch(
                getMerchantById({ id: merchantId })
            )?.unwrap();
            if (merchantDetailsPromise?.success === true) {
                setMerchantDetails(merchantDetailsPromise?.data);
                setIsMerchantDetailOpen(true);
            } else {
                console.error("Failed to fetch merchant details:", merchantDetailsPromise);
            }
        } catch (error) {
            console.error("Error fetching merchant details:", error);
        }
    };

    const openMerchantPopup = (merchantId) => {
        fetchMerchantDetails(merchantId);
    };

    const closeMerchantPopup = () => {
        setIsMerchantDetailOpen(false);
    };

    const handleDeleteMerchant = (id) => {
        setMerchantToDelete(id);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const deletePromise = await dispatch(
                deleteMerchantById({ id: merchantToDelete })
            )?.unwrap();
            if (deletePromise?.success === true) {
                getMerchantsData(searchQuery);
            }
        } catch (error) {
            console.error("Error deleting merchant:", error);
        } finally {
            setShowDeleteConfirm(false);
            setMerchantToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setMerchantToDelete(null);
    };

    return (
        <>
            {loading === true && <Loader />}
            {isMerchantDetailOpen && (
                <DetailsModal
                    handleClose={closeMerchantPopup}
                    content={<PopupContent merchantDetails={merchantDetails} onDelete={handleDeleteMerchant} />}
                    popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
                />
            )}
            {showDeleteConfirm && (
                <CancelPopup
                    handleNo={handleDeleteCancel}
                    handleYes={handleDeleteConfirm}
                    heading="Delete Merchant"
                    content="Are you sure you want to delete this merchant profile? This action cannot be undone."
                    image={deleteImg}
                    confirmation={false}
                    popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
                />
            )}
            <div className="flex justify-between col-span-12">
                <BreadCrumsHeader heading="In-Progress Merchants" items={items} />
                <div className="flex gap-3 items-center">

                    <button
                        onClick={() => navigate("/merchant-list")}
                        className="flex items-center justify-center bg-white text-[#00ADEF] font-[600] text-[14px] h-[38px] px-4 rounded-[5px] transition-colors duration-150 hover:bg-[#F0F9FF] font-nunito border border-[#00ADEF] whitespace-nowrap"
                        style={{
                            boxShadow: "0 2px 8px rgba(0,173,239,0.10)",
                            letterSpacing: 0.1,
                        }}
                    >
                        Merchant List
                    </button>

                </div>
            </div>
            <div className="w-full flex justify-start items-center mt-4">
                <SearchBar
                    parentDivClass="w-[65%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
                    divClassName="relative"
                    inputClassName="searchBar w-full p-3 text-[14px] font-poppins font-[400] outline-none"
                    iconClassName="absolute sm:right-[5%] sm:bottom-[25%] lg:right-[3%]"
                    placeholder="Search"
                    value={searchQuery}
                    handleChange={(e) => handleSearchChange(e.target.value)}
                />
            </div>
            <div id="merchant-list-container" className="col-span-12 mt-4">
                <TableContainer style={{ width: "100%", overflowX: "auto" }}>
                    <Table
                        sx={{
                            minWidth: "800px",
                            borderCollapse: "separate",
                            borderSpacing: "0px 8px",
                        }}
                        aria-label="simple table"
                    >
                        <TableHead className="bg-[#00ADEF]">
                            <TableRow
                                className="h-[50px]"
                                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                            >
                                {columns.map((column) => {
                                    return (
                                        <TableCell key={column.name} className="!text-[#FFFFFF]">
                                            {column.name}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {allMerchants?.map((merchant, index) => {
                                // Helper function to get email (handles both invited and regular merchants)
                                const getEmail = () => merchant?.user?.email || merchant?.email || "N/A";

                                // Helper function to get date (handles both formats)
                                const getDate = () => {
                                    const date = merchant?.createdAt || merchant?.created_at || merchant?.notifiedAt;
                                    if (!date) return "N/A";
                                    return separateDateTimeAndConvertToLocale(date)?.localDate || date;
                                };

                                return (
                                    <TableRow
                                        key={merchant._id}
                                        className="h-[50px]"
                                        sx={{
                                            "&:last-child td, &:last-child th": { border: 0 },
                                            boxShadow: "0px 4px 4px 0px #00000040",
                                            backgroundColor:
                                                (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                                        }}
                                    >
                                        <TableCell className="max-w-[50px]">{(currentPage - 1) * 50 + index + 1}</TableCell>
                                        <TableCell
                                            title={merchant?.businessName}
                                            className="max-w-[120px] truncate"
                                        >
                                            {merchant?.businessName || "N/A"}
                                        </TableCell>
                                        <TableCell
                                            title={getDate()}
                                            className="max-w-[120px] truncate"
                                        >
                                            {getDate()}
                                        </TableCell>
                                        <TableCell
                                            title={getEmail()}
                                            className="max-w-[140px] truncate"
                                        >
                                            {getEmail()}
                                        </TableCell>
                                        <TableCell
                                            title={merchant?.contactPersonNumber || "N/A"}
                                            className="max-w-[120px] truncate"
                                        >
                                            {merchant?.contactPersonNumber || "N/A"}
                                        </TableCell>
                                        <TableCell
                                            title={merchant?.address || "N/A"}
                                            className="max-w-[180px] truncate"
                                        >
                                            {merchant?.address || "N/A"}
                                        </TableCell>
                                        <TableCell className="max-w-[110px]">
                                            <Confirmation status="in-process" />
                                        </TableCell>

                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Pagination
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                />
            </div>
        </>
    );
};

export default InProcessMerchantList;
